import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { detectLocale, getDocumentLang, LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from "./detectLocale";
import { messagesByLocale } from "./locales";
import { createTranslator } from "./translate";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [locale, setLocaleState] = useState(() => detectLocale());

    const setLocale = useCallback((nextLocale) => {
        if (!SUPPORTED_LOCALES.includes(nextLocale)) {
            return;
        }
        setLocaleState(nextLocale);
        localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
        document.documentElement.lang = getDocumentLang(nextLocale);
    }, []);

    useEffect(() => {
        document.documentElement.lang = getDocumentLang(locale);
    }, [locale]);

    const t = useMemo(
        () => createTranslator(messagesByLocale[locale] || messagesByLocale.en),
        [locale]
    );

    const value = useMemo(
        () => ({
            locale,
            setLocale,
            t,
        }),
        [locale, setLocale, t]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within LanguageProvider");
    }
    return context;
}

export function useTranslation() {
    const { locale, setLocale, t } = useLanguage();
    return { locale, setLocale, t };
}

export { messagesByLocale };
